import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, FileText, Shield, Handshake } from 'lucide-react';
import { Button } from './ui/button';
import { getQuotesRoute } from '../lib/customerNav';

const InfoCardsSection = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const infoCardsData = [
    {
      id: 1,
      icon: FileText,
      titleKey: 'additionalServices.priceMatching',
      descKey: 'additionalServices.priceMatchingDesc'
    },
    {
      id: 2,
      icon: Shield,
      titleKey: 'additionalServices.travelInsurance',
      descKey: 'additionalServices.travelInsuranceDesc'
    }
  ];
  
  return (
    <section className="py-24 bg-[#0a0a0a] relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            {t('additionalServices.title')} <span className="text-amber-500">{t('additionalServices.titleHighlight')}</span>
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            {t('additionalServices.subtitle')}
          </p>
        </div>

        {/* Info Cards */}
        <div className="grid md:grid-cols-2 gap-8 mb-16">
          {infoCardsData.map((card) => {
            const IconComponent = card.icon;
            return (
              <div
                key={card.id}
                className="group bg-gradient-to-br from-white/5 to-transparent border border-white/10 rounded-2xl p-8 transition-all duration-500 hover:border-amber-500/50 hover:-translate-y-2"
              >
                {/* Icon */}
                <div className="w-14 h-14 bg-amber-500/10 rounded-xl flex items-center justify-center mb-6 group-hover:bg-amber-500/20 transition-colors">
                  <IconComponent className="w-7 h-7 text-amber-500" />
                </div>

                {/* Content */}
                <h3 className="text-xl font-semibold text-white mb-4 group-hover:text-amber-500 transition-colors">
                  {t(card.titleKey)}
                </h3>
                <p className="text-gray-400 leading-relaxed mb-6">
                  {t(card.descKey)}
                </p>

                {/* Link */}
                <a
                  href="#"
                  className="inline-flex items-center text-amber-500 font-medium hover:gap-3 transition-all duration-300"
                >
                  {t('additionalServices.learnMore')}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </a>
              </div>
            );
          })}
        </div>

        {/* Partnerships */}
        <div className="bg-gradient-to-br from-amber-900/20 to-teal-900/20 rounded-3xl p-8 md:p-12 border border-white/10">
          <div className="text-center mb-12">
            <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
              {t('additionalServices.partnerships')} <span className="text-amber-500">{t('additionalServices.partnershipsHighlight')}</span>
            </h3>
            <p className="text-gray-400">
              {t('additionalServices.partnershipsSubtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* GroomGO Partnership */}
            <div className="bg-black/30 rounded-2xl p-6 border border-white/5">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 bg-amber-500/20 rounded-xl flex items-center justify-center">
                  <span className="text-amber-500 font-bold text-lg">G</span>
                </div>
                <div>
                  <h4 className="text-white font-semibold">{t('additionalServices.groomGoTitle')}</h4>
                  <p className="text-gray-500 text-sm">{t('additionalServices.officialPartnership')}</p>
                </div>
              </div>
              <p className="text-gray-400">
                {t('additionalServices.groomPartnership')}
              </p>
            </div>

            {/* VetGO Partnership */}
            <div className="bg-black/30 rounded-2xl p-6 border border-white/5">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 bg-teal-500/20 rounded-xl flex items-center justify-center">
                  <span className="text-teal-500 font-bold text-lg">V</span>
                </div>
                <div>
                  <h4 className="text-white font-semibold">{t('additionalServices.vetGoTitle')}</h4>
                  <p className="text-gray-500 text-sm">{t('additionalServices.officialPartnership')}</p>
                </div>
              </div>
              <p className="text-gray-400">
                {t('additionalServices.vetPartnership')}
              </p>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center mt-16">
          <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
            {t('additionalServices.rushTitle')}
          </h3>
          <p className="text-gray-400 mb-8">
            {t('additionalServices.rushSubtitle')}
          </p>
          <Button
            size="lg"
            onClick={() => navigate(getQuotesRoute())}
            className="bg-amber-500 hover:bg-amber-600 text-black font-bold px-10 py-6 rounded-full text-lg transition-all duration-300 hover:scale-105"
            data-testid="receive-quotes-btn"
          >
            {t('landing.receiveQuotesNow')}
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </div>
    </section>
  );
};

export default InfoCardsSection;
